// src/components/ReportPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { FaChartBar, FaChartPie, FaClipboardList, FaFileExcel, FaFilePdf } from "react-icons/fa";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";

const API_URL =
  process.env.REACT_APP_API_BASEURL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://sipedasapi.bkkpangkalpinang.com/api";

const COLORS = ["#0088FE","#00C49F","#FFBB28","#FF8042","#845EC2","#4D8076","#FF6F91","#F9F871"];

/* ========= Helpers normalisasi ========= */
const normDate = (r) => r?.date || r?.submission_date || r?.created_at || r?.tanggal || null;

const normStatus = (raw) => {
  const s = String(raw || "").toLowerCase();
  if (["approved","disetujui"].includes(s)) return "Approved";
  if (["pending","menunggu"].includes(s))  return "Pending";
  if (["rejected","ditolak"].includes(s))  return "Rejected";
  if (["sudah dikirim","shipped","terkirim"].includes(s)) return "Sudah Dikirim";
  if (["sudah diterima","delivered","diterima"].includes(s)) return "Sudah Diterima";
  return "Pending";
};

const normItems = (r) => {
  const arr = r?.items || r?.details || r?.cart_items || r?.submission_items || [];
  if (!Array.isArray(arr)) return [];
  return arr.map((it) => ({
    name: it?.name || it?.product_name || "Produk",
    category: it?.category || it?.category_name || it?.kategori || "Lainnya",
    qty: Number(it?.qty ?? it?.quantity ?? 0) || 0,
  }));
};

const normWilker = (r) => {
  const raw = r?.work_area ?? r?.wilayah_kerja ?? r?.user?.work_area;
  if (!raw) return "-";
  return typeof raw === "string" ? raw : (raw?.name || raw?.label || raw?.title || "-");
};

/* ========= Derivasi untuk chart ========= */
const statusSeries = (subs) => {
  const map = {};
  subs.forEach((s) => {
    const st = normStatus(s?.status);
    map[st] = (map[st] || 0) + 1;
  });
  return Object.entries(map).map(([status, total]) => ({ status, total }));
};

const perKategori = (subs) => {
  const map = {};
  subs.forEach((s) => {
    normItems(s).forEach((it) => {
      map[it.category] = (map[it.category] || 0) + it.qty;
    });
  });
  return Object.entries(map).map(([category, jumlah]) => ({ category, jumlah }));
};

const topProduk = (subs, n = 5) => {
  const map = {};
  subs.forEach((s) => {
    normItems(s).forEach((it) => {
      map[it.name] = (map[it.name] || 0) + it.qty;
    });
  });
  return Object.entries(map)
    .map(([name, jumlah]) => ({ name, jumlah }))
    .sort((a, b) => b.jumlah - a.jumlah)
    .slice(0, n);
};

const STATUS_LABEL = {
  Pending: "Menunggu",
  Approved: "Disetujui",
  Rejected: "Ditolak",
  "Sudah Dikirim": "Terkirim",
  "Sudah Diterima": "Diterima",
};

export default function ReportPage({
  submissionHistory: submissionProp = [],
  products: productsProp = [],
  categories: categoriesProp = [],
}) {
  const reportRef = useRef(null);

  // State untuk fetch otomatis bila props kosong
  const [submissions, setSubmissions] = useState([]);
  const [products, setProducts]       = useState([]);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(submissionProp.length === 0);
  const [error, setError]             = useState("");

  useEffect(() => {
    let alive = true;

    if (submissionProp.length || productsProp.length || categoriesProp.length) {
      setSubmissions(submissionProp);
      setProducts(productsProp);
      setCategories(categoriesProp);
      setLoading(false);
      setError("");
      return () => { alive = false; };
    }

    const safeGet = async (url, opts = {}) => {
      try {
        const res = await axios.get(url, {
          withCredentials: false, // <— FIX CORS: jangan kirim cookie
          ...opts,
        });
        const d = res?.data;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.data)) return d.data;
        if (Array.isArray(d?.submissions)) return d.submissions;
        if (Array.isArray(d?.products)) return d.products;
        if (Array.isArray(d?.categories)) return d.categories;
        return [];
      } catch {
        return [];
      }
    };

    (async () => {
      try {
        setLoading(true); setError("");

        const [subs, prods, cats] = await Promise.all([
          safeGet(`${API_URL}/submissions`, { params: { scope: "all", status: "all" } }),
          safeGet(`${API_URL}/products`),
          safeGet(`${API_URL}/categories`),
        ]);

        if (!alive) return;

        setSubmissions(subs);

        // Fallback infer dari submissions bila products/categories kosong
        if (prods.length === 0) {
          const names = new Set();
          subs.forEach((s) => normItems(s).forEach((it) => names.add(it.name)));
          setProducts(Array.from(names).map((n) => ({ name: n })));
        } else setProducts(prods);

        if (cats.length === 0) {
          const names = new Set();
          subs.forEach((s) => normItems(s).forEach((it) => names.add(it.category)));
          setCategories(Array.from(names).map((n) => ({ name: n })));
        } else setCategories(cats);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || "Gagal memuat laporan.");
        setSubmissions([]); setProducts([]); setCategories([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionProp, productsProp, categoriesProp]);

  const subs = submissionProp.length ? submissionProp : submissions;

  // ====== Data untuk semua blok ======
  const statusData    = useMemo(() => statusSeries(subs), [subs]);
  const kategoriData  = useMemo(() => perKategori(subs), [subs]);
  const topProdukData = useMemo(() => topProduk(subs, 5), [subs]);

  // Wilayah kerja: agregasi & ranking
  const { wilkerAll, wilkerTop } = useMemo(() => {
    const agg = {};
    subs.forEach((s) => {
      const wa = normWilker(s) || "-";
      const totalItem = normItems(s).reduce((sum, it) => sum + it.qty, 0);
      if (!agg[wa]) agg[wa] = { wilker: wa, pengajuan: 0, items: 0 };
      agg[wa].pengajuan += 1;
      agg[wa].items     += totalItem;
    });
    const arr = Object.values(agg);
    return {
      wilkerAll: arr.sort((a, b) => b.pengajuan - a.pengajuan),
      wilkerTop: [...arr].sort((a, b) => b.pengajuan - a.pengajuan).slice(0, 5),
    };
  }, [subs]);

  // Ringkasan
  const totalPengajuan = subs.length;
  const totalProduk    = productsProp.length ? productsProp.length : products.length;
  const totalKategori  = categoriesProp.length ? categoriesProp.length : categories.length;

  // Downloaders
  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    window.scrollTo(0, 0);
    const canvas  = await html2canvas(reportRef.current, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf     = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const w       = pdf.internal.pageSize.getWidth();
    const h       = (canvas.height * w) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, w, h);
    pdf.save("laporan-sipedas.pdf");
  };

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();

    const wsStatus = XLSX.utils.json_to_sheet(
      statusData.map((r) => ({ Status: STATUS_LABEL[r.status] || r.status, Jumlah: r.total }))
    );
    XLSX.utils.book_append_sheet(wb, wsStatus, "Status Pengajuan");

    const wsKategori = XLSX.utils.json_to_sheet(
      kategoriData.map((r) => ({ Kategori: r.category, "Jumlah Barang": r.jumlah }))
    );
    XLSX.utils.book_append_sheet(wb, wsKategori, "Barang per Kategori");

    const wsProduk = XLSX.utils.json_to_sheet(
      topProdukData.map((r) => ({ "Nama Produk": r.name, "Jumlah Diajukan": r.jumlah }))
    );
    XLSX.utils.book_append_sheet(wb, wsProduk, "Top 5 Produk");

    // Rekap wilayah kerja
    const wsWilker = XLSX.utils.json_to_sheet(
      wilkerAll.map((r) => ({ "Wilayah Kerja": r.wilker, Pengajuan: r.pengajuan, "Total Item": r.items }))
    );
    XLSX.utils.book_append_sheet(wb, wsWilker, "Wilayah Kerja");

    const wsDetail = XLSX.utils.json_to_sheet(
      subs.flatMap((sub) =>
        normItems(sub).map((it) => ({
          "Nomor Pengajuan": sub?.number || sub?.submission_number || "",
          "Tanggal": normDate(sub) || "",
          "Status": STATUS_LABEL[normStatus(sub?.status)] || normStatus(sub?.status),
          "Nama Produk": it.name,
          "Kategori": it.category,
          "Jumlah": it.qty,
          "Wilayah Kerja": normWilker(sub) || "-",
        }))
      )
    );
    XLSX.utils.book_append_sheet(wb, wsDetail, "Detail Pengajuan");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([out], { type: "application/octet-stream" }), "laporan-sipedas.xlsx");
  };

  return (
    <div className="app-content reports-page" style={{ padding: 24, position: "relative" }}>
      {/* tombol download */}
      <div style={{ position: "absolute", right: 36, top: 20, zIndex: 3, display: "flex", gap: 12 }}>
        <button
          onClick={handleDownloadPDF}
          style={{
            background: "#0088FE", color: "#fff", border: "none", borderRadius: 8,
            padding: "8px 18px", fontWeight: 500, cursor: "pointer", boxShadow: "0 1px 8px #eee",
            display: "flex", alignItems: "center",
          }}
        >
          <FaFilePdf style={{ marginRight: 7, fontSize: 18 }} /> Download PDF
        </button>
        <button
          onClick={handleDownloadExcel}
          style={{
            background: "#27ae60", color: "#fff", border: "none", borderRadius: 8,
            padding: "8px 18px", fontWeight: 500, cursor: "pointer", boxShadow: "0 1px 8px #eee",
            display: "flex", alignItems: "center",
          }}
        >
          <FaFileExcel style={{ marginRight: 7, fontSize: 18 }} /> Download Excel
        </button>
      </div>

      <div ref={reportRef} id="sipedas-laporan-pdf-content" style={{ background: "#f8fbfd", borderRadius: 18, padding: 8 }}>
        <h2 style={{ textAlign: "center", marginBottom: 12 }}>
          <FaClipboardList style={{ marginRight: 8, color: "#0088FE" }} />
          Laporan Statistik Pengajuan
        </h2>

        {error && <div style={{ color: "#d32f2f", textAlign: "center", marginBottom: 12 }}>{error}</div>}

        {/* PIE Status + BAR per Kategori */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 32, marginBottom: 24, justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 8px #eee", padding: 16, minWidth: 330 }}>
            <div style={{ textAlign: "center", marginBottom: 8, fontWeight: 500 }}>
              <FaChartPie style={{ marginRight: 4, color: "#FF8042" }} />
              Statistik Status Pengajuan
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="total"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ status }) => STATUS_LABEL[status] || status}
                >
                  {statusData.map((entry, idx) => (
                    <Cell
                      key={`cell-${idx}`}
                      fill={COLORS[idx % COLORS.length]}
                      fillOpacity={1}
                      stroke="#ffffff"
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [`${v} pengajuan`, "Total"]} />
                <Legend verticalAlign="bottom" height={32} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 8px #eee", padding: 16, minWidth: 380 }}>
            <div style={{ textAlign: "center", marginBottom: 8, fontWeight: 500 }}>
              <FaChartBar style={{ marginRight: 4, color: "#0088FE" }} />
              Jumlah Barang Diajukan per Kategori
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={kategoriData} margin={{ top: 5, right: 8, left: 0, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" style={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(v) => [`${v} item`, "Jumlah"]} />
                <Legend />
                {/* Bar per kategori */}
                <Bar
                  dataKey="jumlah"
                  fill="#0088FE"
                  fillOpacity={1}
                  stroke="#0088FE"
                  strokeWidth={1}
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TOP Produk */}
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 8px #eee", padding: 16, maxWidth: 650, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 8, fontWeight: 500 }}>
            <FaChartBar style={{ marginRight: 4, color: "#00C49F" }} />
            Top 5 Produk Paling Banyak Diajukan
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topProdukData} margin={{ top: 5, right: 8, left: 0, bottom: 15 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" style={{ fontSize: 11 }} interval={0} angle={-15} dy={15} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(v) => [`${v} item`, "Total"]} />
              <Legend />
              {/* Top 5 produk */}
              <Bar
                dataKey="jumlah"
                fill="#00C49F"
                fillOpacity={1}
                stroke="#00C49F"
                strokeWidth={1}
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ==== Wilayah Kerja ==== */}
        <div style={{ background: "#fff", marginTop: 32, borderRadius: 16, boxShadow: "0 1px 8px #eee", padding: 16 }}>
          <div style={{ textAlign: "center", marginBottom: 8, fontWeight: 600 }}>
            Pengajuan &amp; Total Item per Tim Kerja & Wilayah Kerja
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={wilkerAll} margin={{ top: 8, right: 16, left: 0, bottom: 32 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="wilker" interval={0} angle={-15} height={60} textAnchor="end" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {/* Wilayah Kerja */}
              <Bar dataKey="pengajuan" name="Pengajuan" fill="#1976d2" fillOpacity={1} stroke="#1976d2" strokeWidth={1} />
              <Bar dataKey="items"     name="Total Item" fill="#00B386" fillOpacity={1} stroke="#00B386" strokeWidth={1} />
            </BarChart>
          </ResponsiveContainer>

          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: "8px 0" }}>Top 5 Tim Kerja & Wilayah Kerja (berdasarkan jumlah pengajuan)</h4>
            <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
              {wilkerTop.length > 0
                ? wilkerTop.map((row) => (
                    <li key={row.wilker}>
                      <b>{row.wilker}</b> — {row.pengajuan} pengajuan, {row.items} item
                    </li>
                  ))
                : <li>Tidak ada data.</li>}
            </ol>
          </div>
        </div>

        {/* Ringkasan kecil */}
        <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: 30 }}>
          <div style={{ background: "#fafafa", borderRadius: 12, padding: 20, minWidth: 180, textAlign: "center", boxShadow: "0 1px 8px #eee" }}>
            <div style={{ fontWeight: 600, fontSize: 18 }}>{totalPengajuan}</div>
            <div style={{ color: "#888", fontSize: 14 }}>Total Pengajuan</div>
          </div>
          <div style={{ background: "#fafafa", borderRadius: 12, padding: 20, minWidth: 180, textAlign: "center", boxShadow: "0 1px 8px #eee" }}>
            <div style={{ fontWeight: 600, fontSize: 18 }}>{totalProduk}</div>
            <div style={{ color: "#888", fontSize: 14 }}>Total Produk</div>
          </div>
          <div style={{ background: "#fafafa", borderRadius: 12, padding: 20, minWidth: 180, textAlign: "center", boxShadow: "0 1px 8px #eee" }}>
            <div style={{ fontWeight: 600, fontSize: 18 }}>{totalKategori}</div>
            <div style={{ color: "#888", fontSize: 14 }}>Total Kategori</div>
          </div>
        </div>

        <div style={{ textAlign: "right", fontSize: 12, color: "#bbb", marginTop: 36 }}>
          Demo laporan interaktif — SIPEDAS
        </div>
      </div>
    </div>
  );
}
